import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { LatLngTuple } from 'leaflet'
import { debounce } from 'lodash'
import { useRecoilState } from 'recoil'
import { floorDetailState } from 'store/buildingStore'
import {
  currentAnnotation,
  currentPovState,
  povPerPointState
} from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'
import { KrpanoEngine } from 'utils/KrpanoEngine'

interface UseFloorMapProps {
  initialZoomOut?: boolean
  setSelectedPointID: Dispatch<SetStateAction<string>>
  points: any[]
}

export default function useFloorMap({
  initialZoomOut = false,
  setSelectedPointID,
  points
}: UseFloorMapProps) {
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [, setPovPerPoint] = useRecoilState(povPerPointState)
  const [, setCurrentSelected] = useRecoilState(currentAnnotation)
  const [, setCurrentPov] = useRecoilState(currentPovState)
  const [, setSearchParams] = useSearchParams()

  // State management
  const [floorDetail, setFloorDetail] = useRecoilState(floorDetailState)
  const [isZoomOutMap, setIsZoomOutMap] = useState(initialZoomOut)
  const [imgBounds, setImgBounds] = useState<LatLngTuple>(() => {
    const w = floorDetail?.mapFile?.extraInfo?.width
    const h = floorDetail?.mapFile?.extraInfo?.height
    if (w && h) return [h, w]
    return [1000, 1000]
  })
  const [isMapLoading, setIsMapLoading] = useState(false)
  const [showMap, setShowMap] = useState(true)
  const [isZoomTransition, setIsZoomTransition] = useState(false)
  const [zoomAnimation, setZoomAnimation] = useState<
    'expanding' | 'collapsing' | null
  >(null)
  const zoomAnimationTimerRef = useRef<NodeJS.Timeout | null>(null)

  const isFloorChange = useRef<any>(false)
  const povMarkerRef = useRef<any>(null)
  const isButtonZoomingRef = useRef(false)
  const [zoomValue, setZoomValue] = useState({
    zoom: 0,
    minZoom: 0,
    maxZoom: 5
  })
  // state for is SP screen
  const matches = useMediaQuery('(min-width: 768px)')
  const isSmartphone = useMediaQuery('(max-width: 1024px)')
  // Refs
  const mapContainerRef = useRef<any>(null)
  const mapInitializedRef = useRef<boolean>(false)
  const isFittingBoundsRef = useRef<boolean>(false)
  const isZoomingToPointRef = useRef<boolean>(false)
  const prevIsZoomOutMapRef = useRef<boolean>(initialZoomOut)

  // Standard Leaflet bounds setter with zero padding
  const applyMapDimensions = useCallback((w: number, h: number) => {
    const map = mapContainerRef?.current
    if (!map || w <= 0 || h <= 0) return

    const bounds: LatLngTuple[] = [
      [0, 0],
      [h, w]
    ]

    map.setMinZoom(-999)
    map.setMaxBounds(null as any)
    map.invalidateSize({ animate: false })
    map.fitBounds(bounds, {
      animate: false,
      padding: [0, 0]
    })
    map.setMaxBounds(bounds)

    const minZoomLevel = map.getBoundsZoom(bounds)
    map.setView([h / 2, w / 2], minZoomLevel, {
      animate: false
    })
    map.setMinZoom(minZoomLevel)
    map.setMaxZoom(minZoomLevel + 5)

    setZoomValue({
      zoom: minZoomLevel,
      minZoom: minZoomLevel,
      maxZoom: minZoomLevel + 5
    })

    setShowMap(true)
    setIsMapLoading(false)
  }, [])

  // Calculate map size style
  const getMapSizeInlineStyle = useCallback(() => {
    const originalHeight = imgBounds[0] || 1
    const originalWidth = imgBounds[1] || 1
    const ratio = originalWidth / originalHeight
    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight

    let smallWidth = 0
    let smallHeight = 0
    if (isSmartphone) {
      smallWidth = Math.round(windowWidth * 0.3)
      const maxHeight = Math.round(windowHeight * 0.2)
      smallHeight = Math.min(Math.round(smallWidth / ratio), maxHeight)
    } else {
      smallWidth = Math.round(windowWidth * 0.1)
      smallHeight = Math.round(smallWidth / ratio)
    }

    let largeWidth = 0
    let largeHeight = 0
    if (isSmartphone) {
      largeWidth = Math.round(windowWidth * 0.8)
      largeHeight = Math.round(largeWidth / ratio)
    } else {
      largeHeight = Math.round(windowHeight * 0.7)
      largeWidth = Math.round(largeHeight * ratio)
    }

    const scaleX = largeWidth > 0 ? smallWidth / largeWidth : 0.28
    const scaleY = largeHeight > 0 ? smallHeight / largeHeight : 0.28

    const baseStyles: any = {
      willChange: 'transform, width, height',
      padding: 0,
      boxSizing: 'border-box' as const,
      overflow: 'hidden' as const,
      display: 'block' as const,
      transition: 'none',
      transformOrigin: 'top right',
      '--map-scale-x': `${scaleX}`,
      '--map-scale-y': `${scaleY}`
    }

    // If map is minimized (not zoomed out)
    if (!isZoomOutMap) {
      return {
        ...baseStyles,
        width: `${smallWidth}px`,
        height: `${smallHeight}px`
      }
    }

    // If map is maximized (zoomed out)
    return {
      ...baseStyles,
      width: `${largeWidth}px`,
      height: `${largeHeight}px`
    }
  }, [isZoomOutMap, imgBounds, isSmartphone])

  // Handle smooth zoom expand/collapse transition
  const handleZoomToggle = useCallback(() => {
    if (zoomAnimationTimerRef.current) {
      return
    }

    if (!isZoomOutMap) {
      // EXPAND: Mini -> Large
      setIsZoomOutMap(true)
      setZoomAnimation('expanding')
      zoomAnimationTimerRef.current = setTimeout(() => {
        setZoomAnimation(null)
        zoomAnimationTimerRef.current = null
      }, 240)
    } else {
      // COLLAPSE: Large -> Mini
      setZoomAnimation('collapsing')
      zoomAnimationTimerRef.current = setTimeout(() => {
        setIsZoomOutMap(false)
        setZoomAnimation(null)
        zoomAnimationTimerRef.current = null
      }, 200)
    }
  }, [isZoomOutMap])

  const animateMapChange = useCallback((callback: () => void) => {
    setIsZoomTransition(true)
    callback()
  }, [])

  // Initialize map
  const initializeMap = useCallback(() => {
    if (!floorDetail?.mapFile?.s3Path || mapInitializedRef.current) return

    if (!mapContainerRef?.current) {
      requestAnimationFrame(() => {
        initializeMap()
      })
      return
    }

    mapInitializedRef.current = true

    const setupMapWithDimensions = (width: number, height: number) => {
      setImgBounds([height, width])

      requestAnimationFrame(() => {
        applyMapDimensions(width, height)
        setTimeout(() => {
          applyMapDimensions(width, height)
        }, 50)
      })
    }

    // Fast-path: use dimensions if already known from extraInfo
    const extraWidth = floorDetail.mapFile?.extraInfo?.width
    const extraHeight = floorDetail.mapFile?.extraInfo?.height
    if (extraWidth && extraHeight) {
      setupMapWithDimensions(extraWidth, extraHeight)
      return
    }

    setIsMapLoading(true)
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = floorDetail.mapFile.s3Path

    if (img.complete && img.naturalWidth && img.naturalHeight) {
      setupMapWithDimensions(img.naturalWidth, img.naturalHeight)
      return
    }

    img.onload = () => {
      setupMapWithDimensions(
        img.naturalWidth || img.width,
        img.naturalHeight || img.height
      )
    }

    img.onerror = () => {
      setImgBounds([1000, 1000])
      setShowMap(true)
      setIsMapLoading(false)
      mapInitializedRef.current = false
    }
  }, [
    floorDetail?.mapFile?.s3Path,
    floorDetail?.mapFile?.extraInfo,
    applyMapDimensions
  ])

  // Reset map when floor changes
  const resetMap = useCallback(() => {
    if (zoomAnimationTimerRef.current) {
      clearTimeout(zoomAnimationTimerRef.current)
      zoomAnimationTimerRef.current = null
    }
    setZoomAnimation(null)
    mapInitializedRef.current = false
    setIsMapLoading(true)
    setShowMap(false)
    const w = floorDetail?.mapFile?.extraInfo?.width
    const h = floorDetail?.mapFile?.extraInfo?.height
    if (w && h) {
      setImgBounds([h, w])
    } else {
      setImgBounds([1000, 1000])
    }
    setIsZoomTransition(false)
    setZoomValue({
      zoom: 5,
      minZoom: 0,
      maxZoom: 5
    })
  }, [floorDetail?.mapFile?.extraInfo])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (zoomAnimationTimerRef.current) {
        clearTimeout(zoomAnimationTimerRef.current)
      }
    }
  }, [])

  // Zoom handlers - prevent rapid/double-click jitter and unwanted panning while animating
  const handleZoomIn = useCallback(() => {
    const map = mapContainerRef?.current
    if (!map || isButtonZoomingRef.current) return

    const currentZoom = Math.round(map.getZoom())
    const maxZoom = map.getMaxZoom()

    if (currentZoom < maxZoom) {
      isButtonZoomingRef.current = true
      const nextZoom = Math.min(currentZoom + 1, maxZoom)

      map.setZoom(nextZoom, {
        animate: true,
        duration: 0.2
      })

      setZoomValue({
        zoom: nextZoom,
        minZoom: map.getMinZoom(),
        maxZoom
      })

      setTimeout(() => {
        isButtonZoomingRef.current = false
      }, 220)
    }
  }, [])

  const handleZoomOut = useCallback(() => {
    const map = mapContainerRef?.current
    if (!map || isButtonZoomingRef.current) return

    const currentZoom = Math.round(map.getZoom())
    const minZoom = map.getMinZoom()

    if (currentZoom > minZoom) {
      isButtonZoomingRef.current = true
      const nextZoom = Math.max(currentZoom - 1, minZoom)

      map.setZoom(nextZoom, {
        animate: true,
        duration: 0.2
      })

      setZoomValue({
        zoom: nextZoom,
        minZoom,
        maxZoom: map.getMaxZoom()
      })

      setTimeout(() => {
        isButtonZoomingRef.current = false
      }, 220)
    }
  }, [])

  const handleZoomToPoint = useCallback((x: number, y: number) => {
    if (!mapContainerRef?.current) return

    // Prevent rapid clicks
    if (isZoomingToPointRef.current) {
      return
    }

    const map = mapContainerRef.current
    const zoom = map.getZoom()
    const minZoom = map.getMinZoom()
    const maxZoom = map.getMaxZoom()
    // Focus clearly on the target point
    const targetZoom = Math.min(Math.max(zoom + 1.5, minZoom + 2.5), maxZoom)

    // Mark as zooming and disable dragging
    isZoomingToPointRef.current = true
    map.dragging.disable()

    // Smooth flyTo animation for centering and zooming simultaneously
    map.flyTo([y, x], targetZoom, {
      duration: 0.25,
      easeLinearity: 0.5
    })

    // Re-enable dragging after animation (250ms + buffer)
    setTimeout(() => {
      if (mapContainerRef?.current) {
        mapContainerRef.current.dragging.enable()
        isZoomingToPointRef.current = false
      }
    }, 280)
  }, [])

  const handleResetZoom = useCallback(() => {
    const map = mapContainerRef?.current
    if (!map || isButtonZoomingRef.current) return

    isButtonZoomingRef.current = true
    const minZoom = map.getMinZoom()
    const center: LatLngTuple = [imgBounds[0] / 2, imgBounds[1] / 2]

    // Reset to minimum zoom level and center the map
    map.setView(center, minZoom, {
      animate: true,
      duration: 0.2,
      easeLinearity: 0.5
    })

    // Update zoom state
    setZoomValue({
      zoom: minZoom,
      minZoom,
      maxZoom: map.getMaxZoom()
    })

    setTimeout(() => {
      isButtonZoomingRef.current = false
    }, 220)
  }, [imgBounds])

  // Get the inline style for the floor select container that matches the map size
  const getFloorSelectInlineStyle = useCallback(() => {
    // Floor list width:
    //   PC: 10% of screen width
    //   SP: 30% of screen width
    const windowWidth = window.innerWidth

    let width: number
    if (isSmartphone) {
      // SP: 30% of total screen width
      width = Math.round(windowWidth * 0.3)
    } else {
      // PC: 10% of total screen width
      width = Math.round(windowWidth * 0.1)
    }

    return {
      backgroundColor: '#5F9EA0',
      width: `${width}px`,
      height: '40px',
      padding: '0rem',
      fontSize: matches ? '14px' : '12px'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, isSmartphone])

  // Handle floor selection
  const handleSelectFloor = (value: number) => {
    isFloorChange.current = true
    // Reset states
    resetMap()

    // Update URL
    setSearchParams((params) => {
      params.set('floor', String(value))
      params.delete('point')
      params.delete('area')
      params.delete('subimage')
      params.delete('hlookat')
      params.delete('vlookat')
      params.delete('fov')
      return params
    })

    // Update selected floor
    // Keep the old selectedPoint so the panorama continues showing the previous floor's image
    // under the loading overlay. The new point will be set when the new floor's data arrives
    // via the data effect in index.tsx.
    setCurrentViewer((prev) => ({
      ...prev,
      selectedArea: undefined,
      // selectedPoint: intentionally kept — prevents panorama from resetting immediately
      selectedFloor: value,
      isClickToNextFloor: false,
      isSelectPointInLeftMenu: true,
      isClickFromTourCarousel: false
    }))
  }

  // Handle marker click
  const handleMarkerClick = (point: any) => {
    // Snapshot POV of the point we are leaving so it can be restored on return
    if (
      currentViewer.pointDetail?.id &&
      currentViewer.pointDetail.id !== point.id
    ) {
      const embedpano = new KrpanoEngine('viewer').instance
      if (embedpano) {
        const h = Number(embedpano.get('view.hlookat'))
        const v = Number(embedpano.get('view.vlookat'))
        const z = Number(embedpano.get('view.fov'))
        if (!Number.isNaN(h) && !Number.isNaN(v) && !Number.isNaN(z)) {
          const leavingId = currentViewer.pointDetail.id
          setPovPerPoint((prev) => ({ ...prev, [leavingId]: { h, v, z } }))
        }
      }
    }
    // Reset currentPov so the viewer uses the point's default initView
    setCurrentPov(null)
    // Also clear the stored POV for the target point so the viewer falls through
    // to initView instead of reusing the last camera angle from povPerPoint
    setPovPerPoint((prev) => {
      const next = { ...prev }
      delete next[point.id]
      return next
    })
    setCurrentSelected(undefined)

    // Update URL
    const urlToUpdate = new URL(window.location.href)
    urlToUpdate.searchParams.set('point', point.id)
    urlToUpdate.searchParams.set('area', String(point.areaId))
    urlToUpdate.searchParams.delete('subimage')
    urlToUpdate.searchParams.delete('hlookat')
    urlToUpdate.searchParams.delete('vlookat')
    urlToUpdate.searchParams.delete('fov')
    window.history.pushState({}, '', urlToUpdate.toString())

    setSelectedPointID(point.id)
    setCurrentViewer((prev) => ({
      ...prev,
      selectedArea: `${point.areaId}`,
      selectedPoint: point.id,
      selectedImage: point.image360?.id,
      // Map click should reset POV to default (treat like left-menu selection)
      isSelectPointInLeftMenu: true,
      isClickToDirection: false,
      isClickFromTourCarousel: false
    }))
  }

  // Keep Leaflet map bounds synchronized whenever container element resizes
  useEffect(() => {
    const map = mapContainerRef?.current
    if (!map) return

    const container = map.getContainer?.()
    if (!container || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      if (!mapContainerRef?.current) return
      const [h, w] = imgBounds
      if (h <= 0 || w <= 0 || (h === 1000 && w === 1000)) return

      if (!isZoomingToPointRef.current && !isFittingBoundsRef.current) {
        applyMapDimensions(w, h)
      }
    })

    observer.observe(container)
    return () => observer.disconnect()
  }, [imgBounds, applyMapDimensions])

  // Fallback resize listener
  useEffect(() => {
    const handleResize = debounce(() => {
      if (
        imgBounds[0] > 0 &&
        imgBounds[1] > 0 &&
        !(imgBounds[0] === 1000 && imgBounds[1] === 1000)
      ) {
        applyMapDimensions(imgBounds[1], imgBounds[0])
      }
    }, 150)

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [imgBounds, applyMapDimensions])

  // Handle zoom state changes synchronously before browser paints to prevent top-left glitch
  useLayoutEffect(() => {
    if (prevIsZoomOutMapRef.current === isZoomOutMap) {
      return
    }
    prevIsZoomOutMapRef.current = isZoomOutMap

    if (mapContainerRef?.current && showMap && !isMapLoading) {
      isFittingBoundsRef.current = true

      if (imgBounds[0] > 0 && imgBounds[1] > 0) {
        applyMapDimensions(imgBounds[1], imgBounds[0])
      }

      requestAnimationFrame(() => {
        isFittingBoundsRef.current = false
      })
    }
  }, [isZoomOutMap, showMap, isMapLoading, imgBounds, applyMapDimensions])

  // Add zoom event listener - update state after animation completes
  useEffect(() => {
    const map = mapContainerRef?.current
    if (!map) return

    const handleZoomEnd = () => {
      // Skip update if we're currently fitting bounds to avoid infinite loop
      if (isFittingBoundsRef.current) return

      // Update zoom value after animation completes
      const currentZoom = map.getZoom()
      const minZoom = map.getMinZoom()
      const maxZoom = map.getMaxZoom()

      setZoomValue({
        zoom: currentZoom,
        minZoom,
        maxZoom
      })
    }

    // Listen to zoomend to get final value after animation
    map.on('zoomend', handleZoomEnd)
    // Only listen to moveend for user interactions, not programmatic fitBounds
    const handleMoveEnd = () => {
      if (!isFittingBoundsRef.current) {
        handleZoomEnd()
      }
    }
    map.on('moveend', handleMoveEnd)

    // Clean up the event listener on unmount or when dependencies change
    return () => {
      map.off('zoomend', handleZoomEnd)
      map.off('moveend', handleMoveEnd)
    }
  }, [showMap, isMapLoading, floorDetail?.id])

  // Initialize map when URL changes
  useEffect(() => {
    if (floorDetail?.mapFile?.s3Path) {
      mapInitializedRef.current = false
      initializeMap()
    }
  }, [floorDetail?.mapFile?.s3Path, initializeMap])

  useEffect(() => {
    if (floorDetail && !floorDetail?.mapFile?.s3Path) {
      // Ensure map loading state is cleared so parent can proceed
      setIsMapLoading(false)
      setShowMap(false)
      setImgBounds([1000, 1000])
      mapInitializedRef.current = false
    }
  }, [floorDetail])

  // effect set default point when select floor from floor select in right  screen of tour viewer
  useEffect(() => {
    if (isFloorChange.current) {
      isFloorChange.current = false
      const defaultPoint = [...(points || [])].sort(
        (a, b) => a.order - b.order
      )[0]

      setCurrentViewer((prev) => ({
        ...prev,
        selectedPoint: defaultPoint?.id,
        selectedArea: `${defaultPoint?.areaId}`,
        selectedImage: defaultPoint?.image360?.id
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points])

  return {
    showMap,
    imgBounds,
    isZoomOutMap,
    zoomAnimation,
    isMapLoading,
    isZoomTransition,
    currentViewer,
    mapContainerRef,
    resetMap,
    handleZoomIn,
    handleZoomOut,
    animateMapChange,
    handleZoomToggle,
    handleZoomToPoint,
    handleResetZoom,
    handleSelectFloor,
    setCurrentViewer,
    handleMarkerClick,
    getMapSizeInlineStyle,
    getFloorSelectInlineStyle,
    povMarkerRef,
    floorDetail,
    setFloorDetail,
    zoomValue
  }
}
